import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { tagOrderIndex, sortByTagOrder } from '@lib/tag-order';
import { computed, type ComputedRef } from 'vue';
import type { ProductImageTagCategoryDto, ProductImageTagDto } from '@/ApiClient';

export const useProductImageTags = defineStore('product-image-tags', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.productImageTag_GetCategories,
    dto: 'ProductImageTagCategoryDto',
    create: api.productImageTag_CreateCategory,
    delete: api.productImageTag_DeleteCategory,
    update: api.productImageTag_UpdateCategory,
    signalR: 'ProductImageTagUpdate',
  });
  const { loaded } = globalStoreBase;

  const any: ComputedRef<boolean> = computed(() => {
    return Boolean(loaded.value.length && loaded.value.find((g) => g.tags && g.tags.length > 0));
  });

  const sorted: ComputedRef<ProductImageTagCategoryDto[]> = computed(() => {
    return loaded.value
      .slice()
      .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)
      .map((g) => ({
        ...g,
        tags: g.tags?.slice().sort((a, b) => a.name?.localeCompare(b.name || '') || 0),
      }));
  });

  const flattened: ComputedRef<ProductImageTagDto[]> = computed(() => loaded.value.flatMap((g) => g.tags || []));

  const baseCategory = computed(() => loaded.value[0]);

  async function addNewTag(name: string, color: string, tagCategoryId: number) {
    const brandId = useUser().brandId;
    const tag = await api.productImageTag_CreateTag(brandId, { name, color, tagCategoryId });

    const category = loaded.value.find((c) => c.id === tagCategoryId);
    if (category) {
      if (!category.tags) category.tags = [];
      category.tags.push(tag);
    }
    return tag;
  }

  async function updateTag(tagId: number, name: string, color: string) {
    const brandId = useUser().brandId;
    const tag = await api.productImageTag_UpdateTag(brandId, tagId, { color, name });

    const category = loaded.value.find((c) => c.id === tag.tagCategoryId);
    if (category) {
      const idx = category.tags?.findIndex((t) => t.id === tagId);
      if (idx !== undefined && idx > -1) category.tags?.splice(idx, 1, tag);
    }
    return tag;
  }

  async function deleteTag(tagId: number) {
    const brandId = useUser().brandId;
    await api.productImageTag_DeleteTag(brandId, tagId);

    const category = loaded.value.find((c) => c.tags?.some((t) => t.id == tagId));
    if (category) {
      const idx = category.tags!.findIndex((t) => t.id == tagId);
      category.tags!.splice(idx, 1);
    }
  }

  function idsToTags(ids: number[]) {
    const tags: ProductImageTagDto[] = [];
    for (const id of ids) {
      const tag = getTagById(id);
      if (tag) tags.push(tag);
    }
    return tags;
  }

  function getTagById(id: number): ProductImageTagDto | undefined {
    return flattened.value.find((x) => x.id == id);
  }

  function getCategoryById(id: number): ProductImageTagCategoryDto | undefined {
    return loaded.value.find((x) => x.id == id);
  }

  // One palette-order Map per store: pill components sort tag ids through this instead of every
  // cell instance building its own Map over the whole palette.
  const orderIndexById: ComputedRef<Map<number, number>> = computed(() => tagOrderIndex(sorted.value));

  function sortByOrder<T extends { id: number }>(tags: T[]): T[] {
    return sortByTagOrder(tags, orderIndexById.value);
  }

  return {
    ...globalStoreBase,
    any,
    sorted,
    orderIndexById,
    sortByOrder,
    flattened,
    baseCategory,
    addNewCategory: globalStoreBase.create,
    updateCategory: globalStoreBase.update,
    deleteCategory: globalStoreBase.delete,
    addNewTag,
    updateTag,
    deleteTag,
    idsToTags,
    getTagById,
    getCategoryById,
  };
});
